import { Dropdown, Menu, Select } from 'antd';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../baseComponents';
import { ActionMenuIcon, EditAppointmentIcon, PlusIcon } from 'src/icons';
import { SuperAdminTable } from '../../../baseComponents/Table';
import { useDispatch } from 'react-redux';
import {
  getAllConditionListAction,
  getAllPractitionerListAction,
} from 'src/modules/SuperAdmin/store/actions';
import { Sort } from 'src/icons/Sort.icon';
import { useSelector } from 'react-redux';
import AddPractitionerPage from './AddPractitioner.page';
import EditPractitionerPage from './EditPractitioner.page';
import {
  getPractitionerByIdAction,
  getPromptByConditionAction,
  getPractitionersOrgLevel,
} from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import EditPromptPage from '../Prompts/EditPrompt.page';
import EditPromptoverrides from '../PromptsOverride/EditPromptoverrides';
import { ProfileLogo } from 'src/components';
import { getLLmVersiosnsActions } from 'src/modules/SuperAdmin/store/actions/prompts.action';
import { getFromStorage, storeInLocal } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';

const AllPractitionersPage = () => {
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const dispatch = useDispatch();
  const [persistedOrgUUID, setPersistedOrgUUID] = useState(() => {
    const savedOrg = getFromStorage('superAdminSelectedOrganization');
    const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
    return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid;
  });

  const { getAllPractitionersList, practitionersOrgLevel } = useSelector(
    (state) => state?.doctorsState
  );
  const { isLoading } = useSelector((state) => state?.conditionState);
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  useEffect(() => {
    dispatch(getLLmVersiosnsActions());
    dispatch(getAllConditionListAction());
    dispatch(getAllActiveOrganizationAction());
    dispatch(getAllPractitionerListAction(persistedOrgUUID));
    dispatch(getPractitionersOrgLevel(persistedOrgUUID));
  }, [dispatch, persistedOrgUUID]);

  const [tableData, setTableData] = useState([]);
  const [tableLoader, setTableLoader] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { getPromptById } = useSelector((state) => state?.promptsState);
  const { getPromptOverridesById } = useSelector(
    (state) => state?.promptOverrideState
  );
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  const [editPromptOverrideOpen, setEditPromptOverrideOpen] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);

  useEffect(() => {
    if (getPromptById?.label === 'global') {
      setEditPromptOpen(true);
    } else if (getPromptOverridesById?.label === 'prompt_override') {
      setEditPromptOverrideOpen(true);
    }
  }, [getPromptById, getPromptOverridesById]);

  useEffect(() => {
    setTableLoader(isLoading);
  }, [isLoading]);

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  };

  useEffect(() => {
    if (
      getAllPractitionersList &&
      Array.isArray(getAllPractitionersList) &&
      getAllPractitionersList.length > 0
    ) {
      let dataArray = getAllPractitionersList.map((list) => {
        let conditionNames = {};
        list.conditions.forEach((condition) => {
          conditionNames[condition.condition_id] = condition.condition_name;
        });
        let conditionNameString = Object.values(conditionNames).join(', ');
        return {
          practitioner_conditions_id: list.practitioner_conditions_id,
          practitioner_id: list.practitioner_id,
          practitioner_name: list.practitioner_name
            ? capitalizeFirstLetter(list.practitioner_name)
            : 'N/A',
          condition_name: conditionNameString ? conditionNameString : null,
          organization_name: list?.practitioner_organization
            ? list?.practitioner_organization
            : 'N/A',
          conditionNames: conditionNames, // Adding this to keep track of condition IDs
          practitioner_email: list?.practitioner_email
            ? list?.practitioner_email
            : 'N/A',
          normalized_practitioner_name: list.practitioner_name
            ? list.practitioner_name.replace(/^Dr\.\s*/, '').replace(/\s+/g, '')
            : 'N/A',
          title: null,
        };
      });
      setTableData(dataArray);
    } else {
      setTableData([]);
    }
  }, [getAllPractitionersList]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (getAllPractitionersList && Array.isArray(getAllPractitionersList)) {
      let dataArr = [];
      getAllPractitionersList.forEach((key) => {
        dataArr.push({
          value: key?.practitioner_id,
          label: capitalizeFirstLetter(key?.practitioner_name),
        });
      });
    }
  }, [getAllPractitionersList]);

  const handleEditOpen = (id) => {
    dispatch(
      getPractitionerByIdAction(id, setEditModalOpen, null, persistedOrgUUID)
    );
  };

  const menu_options = (id, record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editAdminOverride') {
            handleEditOpen(id, record);
          }
        }}
      >
        <Menu.Item key="editAdminOverride">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit user</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const doctor_nameSorter = (a, b) => {
    const nameA = a.normalized_practitioner_name;
    const nameB = b.normalized_practitioner_name;

    return nameA.localeCompare(nameB);
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Practitioner name{' '}
          {tableData?.length > 0 && <Sort className={'sort-icon-doctor'} />}
        </span>
      ),
      dataIndex: 'practitioner_name',
      key: 'practitioner_name',
      width: '20%',
      sorter: tableData?.length > 0 && doctor_nameSorter,
      render: (text, record) => {
        const nameWithoutTitle = record.practitioner_name?.replace('Dr. ', '');
        const [firstName, lastName] = nameWithoutTitle.split(' ');
        return (
          <>
            <div className="flex items-center ">
              <ProfileLogo
                practitionersOrgLevel={practitionersOrgLevel}
                name={`${firstName} ${lastName}`}
                className="profile-popover-logo mr-1 sm:mr-2 lg:mr-3"
                practitioner_name={nameWithoutTitle}
              />
              <div className="">{record.practitioner_name}</div>
            </div>
          </>
        );
      },
    },
    {
      title: <span className="font-[700]"> Email</span>,
      dataIndex: 'practitioner_email',
      key: 'practitioner_email',
      width: '20%',
    },
    {
      title: <span className="font-[700]"> Organization</span>,
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: '20%',
    },
    {
      title: <span className="font-[700]"> Condition name</span>,
      dataIndex: 'condition_name',
      key: 'condition_name',
      width: '60%',
      render: (text, record) => {
        // Simple hash function to generate a color from a string (condition ID)
        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          let color = '#';
          for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += ('00' + value.toString(16)).substr(-2);
          }
          return color;
        };
        const conditionNames = text
          ?.split(',')
          ?.map((condition) => condition?.trim());
        return (
          <div className="flex flex-wrap gap-2">
            {conditionNames?.map((condition, index) => {
              const color = stringToColor(condition);
              const handleConditionClick = () => {
                const conditionId = Object.keys(record?.conditionNames).find(
                  (key) => record?.conditionNames[key] === condition
                );
                const payload = {
                  practitioner_id: record && record?.practitioner_id,
                  condition_id: conditionId && parseInt(conditionId),
                };
                dispatch(getPromptByConditionAction(persistedOrgUUID, payload));
              };
              return (
                <div
                  key={index}
                  className="flex items-center px-3 py-1 gap-1 mt-2 w-fit rounded-3xl cursor-pointer"
                  style={{ border: `2px solid ${color}` }}
                  onClick={handleConditionClick}
                >
                  {condition}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: <span className="font-[700]"> Title</span>,
      dataIndex: 'title',
      key: 'title',
      width: '20%',
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      width: '25%',
      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menu_options(record?.practitioner_id, record)}
          >
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const onChangeHandler = useCallback(
    (e, value, field) => {
      if (field === 'organization') {
        const orgUUID = value || currentPractitioner?.org_uuid;
        setPersistedOrgUUID(orgUUID);
        storeInLocal('selectedOrganizationUUID', orgUUID);
        dispatch(getAllPractitionerListAction(orgUUID, null));
        dispatch(getPractitionersOrgLevel(orgUUID));
        setSelectedPractitioner('Select practitioner');
      } else if (field === 'practitioner') {
        if (value?.value === 'all') {
          setSelectedPractitioner('All');
          dispatch(getAllPractitionerListAction(persistedOrgUUID, null));
        } else {
          dispatch(
            getAllPractitionerListAction(persistedOrgUUID, value?.value)
          );
          setSelectedPractitioner([value]);
        }
      }
    },
    [currentPractitioner?.org_uuid, dispatch, persistedOrgUUID]
  );

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setPersistedOrgUUID(newOrg.org_uuid);
        onChangeHandler(null, newOrg.org_uuid, 'organization');
      }
    );

    return () => unsubscribe();
  }, [onChangeHandler]);

  function hasMultiplePractitionersWithSameName(name) {
    const practitionersWithSameName = practitionersOrgLevel?.filter(
      (practitioner) => practitioner.practitioner_name === name
    );
    return practitionersWithSameName.length > 1;
  }

  return (
    <>
      <div>
        <Formik>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center">
                <div className="flex justify-start gap-12">
                  <div className="bg-white shadow-sm rounded-xl w-60 flex items-center">
                    <Select
                      style={{
                        placeholder: {
                          fontFamily: 'Poppins',
                          color: '#667085',
                        },
                      }}
                      id="search_practitioner_Practitioner"
                      // options={practitionerOptions}
                      value={selectedPractitioner}
                      onChange={(e, value) =>
                        onChangeHandler(e, value, 'practitioner')
                      }
                      bordered={false}
                      className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
                      optionFilterProp="label"
                      placeholder="Search practitioner"
                      showSearch
                    >
                      {practitionersOrgLevel.length > 0 && (
                        <Select.Option key="all" value="all" label="All">
                          All
                        </Select.Option>
                      )}
                      {practitionersOrgLevel?.map((names, idx) => {
                        const nameWithoutTitle =
                          names.practitioner_name?.replace('Dr. ', '');
                        const [firstName, lastName] =
                          nameWithoutTitle.split(' ');

                        return (
                          <Select.Option
                            key={idx}
                            value={names.practitioner_id}
                            label={names.practitioner_name}
                            email={names.practitioner_email}
                          >
                            <div className="font-sans flex">
                              <div className="flex items-center">
                                {!selectedPractitioner?.includes(
                                  names.practitioner_id
                                ) && (
                                  <ProfileLogo
                                    practitionersOrgLevel={
                                      practitionersOrgLevel
                                    }
                                    name={`${firstName} ${lastName}`}
                                    className="profile-popover-logo mr-1 sm:mr-2 lg:mr-3"
                                    practitioner_name={nameWithoutTitle}
                                  />
                                )}
                                <div className="">
                                  {names.practitioner_name}
                                  {hasMultiplePractitionersWithSameName(
                                    names.practitioner_name
                                  ) && (
                                    <div
                                      style={{
                                        color: 'grey',
                                        fontSize: 'smaller',
                                        fontFamily: 'Poppins',
                                      }}
                                    >
                                      {/* {selectedPractitioner &&
                                                                                !selectedPractitioner.includes(names?.practitioner_id) && ( */}
                                      <span>{names.practitioner_email}</span>
                                      {/* )} */}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end sm:ml-4 mt-4 sm:mt-0">
                  <Button
                    type={'button'}
                    onClick={() => setAddModalOpen(true)}
                    className="flex items-center"
                  >
                    <PlusIcon className="mr-2" /> Add user
                  </Button>
                </div>
              </div>

              <div className="mt-5 ant-table rounded-xl shadow-xl">
                <SuperAdminTable
                  className={`appointment-page-table`}
                  columns={columns}
                  data={tableData}
                  loading={tableLoader}
                />
              </div>
              {/* Modal open here : Start */}
              <AddPractitionerPage
                isOpen={addModalOpen}
                setIsOpen={setAddModalOpen}
                selectedOrganizationUUID={persistedOrgUUID}
                setSelectedPractitioner={setSelectedPractitioner}
                persistedOrgUUID={persistedOrgUUID}
                selectedPractitioner={selectedPractitioner}
                setPersistedOrgUUID={setPersistedOrgUUID}
              />

              <EditPractitionerPage
                isOpen={editModalOpen}
                setIsOpen={setEditModalOpen}
                selectedOrganizationUUID={persistedOrgUUID}
                setSelectedPractitioner={setSelectedPractitioner}
              />

              <EditPromptPage
                isOpen={editPromptOpen}
                setIsOpen={setEditPromptOpen}
              />
              <EditPromptoverrides
                isOpen={editPromptOverrideOpen}
                setIsOpen={setEditPromptOverrideOpen}
                selectedOrganizationUUID={persistedOrgUUID}
              />
              {/* Ends */}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AllPractitionersPage;
